import { useEffect } from 'react';

function upsertMetaTag({ name, property, content }) {
  const selector = name ? `meta[name="${name}"]` : `meta[property="${property}"]`;
  const attributeName = name ? 'name' : 'property';
  const attributeValue = name ?? property;

  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attributeName, attributeValue);
    document.head.appendChild(el);
  }

  const previousContent = el.getAttribute('content');
  el.setAttribute('content', content);
  return () => {
    if (previousContent === null) {
      el.remove();
    } else {
      el.setAttribute('content', previousContent);
    }
  };
}

function upsertLinkTag({ rel, href }) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }

  const previousHref = el.getAttribute('href');
  el.setAttribute('href', href);
  return () => {
    if (previousHref === null) {
      el.remove();
    } else {
      el.setAttribute('href', previousHref);
    }
  };
}

function upsertJsonLd({ id, data }) {
  let el = document.head.querySelector(`script[type="application/ld+json"]#${id}`);
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = id;
    document.head.appendChild(el);
  }

  const previousText = el.textContent;
  el.textContent = JSON.stringify(data);
  return () => {
    if (!previousText) {
      el.remove();
    } else {
      el.textContent = previousText;
    }
  };
}

export default function StartImportingPage() {
  useEffect(() => {
    const title = 'Start Importing from China | Checkstall';
    const description =
      'Start importing products from China to Nigeria with Checkstall. Source products, pay safely, and ship at affordable rates.';
    const canonicalUrl = `${window.location.origin}/start-importing`;

    const cleanups = [];

    const previousTitle = document.title;
    document.title = title;
    cleanups.push(() => {
      document.title = previousTitle;
    });

    cleanups.push(upsertMetaTag({ name: 'description', content: description }));
    cleanups.push(upsertMetaTag({ name: 'robots', content: 'index,follow' }));
    cleanups.push(upsertMetaTag({ name: 'googlebot', content: 'index,follow' }));
    cleanups.push(upsertLinkTag({ rel: 'canonical', href: canonicalUrl }));

    cleanups.push(upsertMetaTag({ property: 'og:title', content: title }));
    cleanups.push(upsertMetaTag({ property: 'og:description', content: description }));
    cleanups.push(upsertMetaTag({ property: 'og:type', content: 'website' }));
    cleanups.push(upsertMetaTag({ property: 'og:url', content: canonicalUrl }));

    cleanups.push(upsertMetaTag({ name: 'twitter:card', content: 'summary_large_image' }));
    cleanups.push(upsertMetaTag({ name: 'twitter:title', content: title }));
    cleanups.push(upsertMetaTag({ name: 'twitter:description', content: description }));

    cleanups.push(
      upsertJsonLd({
        id: 'ld-start-importing',
        data: {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: title,
          description,
          url: canonicalUrl,
          isPartOf: {
            '@type': 'WebSite',
            name: 'Checkstall',
            url: window.location.origin
          }
        }
      })
    );

    return () => {
      cleanups.reverse().forEach((fn) => fn());
    };
  }, []);

    return (
      <div className="min-h-screen bg-white text-gray-900">
        {/* HERO SECTION */}
        <section className="relative overflow-hidden bg-gradient-to-br from-red-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-red-100 text-red-700 text-sm font-semibold mb-6">
                Trusted China Import Platform For Nigerians
              </div>
  
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-black leading-tight mb-6">
                Import Products From China <span className="text-[#DC143C]">Without Stress</span>
              </h1>
  
              <p className="text-lg md:text-xl text-gray-600 leading-relaxed mb-8 max-w-2xl">
                We help Nigerians source products directly from China, make payments safely,
                and ship goods to Nigeria at affordable rates.
              </p>
  
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="https://checkstall.com/download"
                  className="bg-[#DC143C] hover:bg-red-700 text-white px-8 py-4 rounded-2xl text-lg font-bold text-center transition-all shadow-lg"
                >
                  Download App
                </a>
  
                <a
                  href="https://app.checkstall.com"
                  className="border-2 border-[#DC143C] text-[#DC143C] hover:bg-red-50 px-8 py-4 rounded-2xl text-lg font-bold text-center transition-all"
                >
                  Visit Our Store
                </a>
              </div>
  
              <div className="flex flex-wrap items-center gap-6 mt-10 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  Safe Payment Support
                </div>
  
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                   Delivery to Nigeria
                </div>
  
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  Verified Suppliers
                </div>
              </div>
            </div>
  
            <div className="relative">
              <div className="bg-white rounded-3xl shadow-2xl p-4 border border-gray-100">
                <img
                  src="https://checkstall-storage.s3.eu-west-1.amazonaws.com/start-importing/6.jpg"
                  alt="China import"
                  className="w-full h-[450px] object-cover rounded-2xl"
                />
              </div>
  
              <div className="mt-6 bg-white rounded-2xl shadow-xl p-5 w-full max-w-sm mx-auto md:mx-0 md:w-64 md:absolute md:-bottom-6 md:-left-6">
                <p className="text-sm text-gray-500 mb-2">Average Savings</p>
                <h3 className="text-3xl font-black text-[#DC143C]">30% - 60%</h3>
                <p className="text-sm text-gray-600 mt-1">
                  compared to local market prices
                </p>
              </div>
            </div>
          </div>
        </section>
  
        {/* VIDEO SECTION */}
        <section className="py-16 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="mb-12">
              <h2 className="text-3xl md:text-4xl font-black mb-4">
                Watch How Checkstall Import Works
              </h2>
  
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Learn how Nigerians are importing products directly from China with less stress,
                better prices, and reliable shipping.
              </p>
            </div>
  
            <div className="rounded-3xl overflow-hidden shadow-2xl border border-gray-100">
              <div className="aspect-video bg-black">
                <iframe
                  className="w-full h-full"
                  src="https://www.youtube-nocookie.com/embed/bWyyLTYTebM?rel=0&modestbranding=1"
                  title="How Checkstall Import Works"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
  
        {/* HOW IT WORKS */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-5xl font-black mb-4">
                How It Works
              </h2>
  
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Start importing from China in just a few simple steps.
              </p>
            </div>
  
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                {
                  step: '01',
                  title: 'Find Products',
                  desc: 'Search for products you want to buy or sell in Nigeria.'
                },
                {
                  step: '02',
                  title: 'We Help You Source',
                  desc: 'We assist you in finding reliable suppliers and negotiating prices.'
                },
                {
                  step: '03',
                  title: 'Pay Safely',
                  desc: 'Make secure payments without worrying about scams or fake suppliers.'
                },
                {
                  step: '04',
                  title: 'Ship To Nigeria',
                  desc: 'Your goods are shipped and delivered safely to Nigeria.'
                }
              ].map((item, index) => (
                <div
                  key={index}
                  className="bg-white rounded-3xl p-8 shadow-lg border border-gray-100 hover:-translate-y-2 transition-all"
                >
                  <div className="text-5xl font-black text-[#DC143C] mb-6">
                    {item.step}
                  </div>
  
                  <h3 className="text-2xl font-bold mb-4">
                    {item.title}
                  </h3>
  
                  <p className="text-gray-600 leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
  
        {/* PRODUCT SHOWCASE */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
              <div>
                <h2 className="text-3xl md:text-5xl font-black mb-4">
                  Popular Products Nigerians Import
                </h2>
  
                <p className="text-lg text-gray-600 max-w-3xl">
                  Thousands of profitable products can be sourced directly from China.
                </p>
              </div>
  
              <a
                href="https://checkstall.com"
                className="text-[#DC143C] font-bold text-lg"
              >
                View More Products →
              </a>
            </div>
  
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {[
                {
                  name: 'Rechargeable Fans',
                  image: 'https://checkstall-storage.s3.eu-west-1.amazonaws.com/start-importing/1.jpg'
                },
                {
                  name: 'Solar Products',
                  image: 'https://checkstall-storage.s3.eu-west-1.amazonaws.com/start-importing/2.jpg'
                },
                {
                  name: 'Fashion Items',
                  image: 'https://checkstall-storage.s3.eu-west-1.amazonaws.com/start-importing/3.jpg'
                },
                {
                  name: 'Electronics',
                  image: 'https://checkstall-storage.s3.eu-west-1.amazonaws.com/start-importing/4.jpg'
                }
              ].map((item, index) => (
                <div
                  key={index}
                  className="group bg-white rounded-3xl overflow-hidden shadow-lg border border-gray-100 hover:-translate-y-2 transition-all"
                >
                  <div className="overflow-hidden">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-full h-56 object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
  
                  <div className="p-5">
                    <h3 className="text-xl font-bold mb-2">{item.name}</h3>
  
                    <p className="text-gray-600 text-sm">
                      Source directly from trusted Chinese suppliers.
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
  
        {/* TRUST SECTION */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-5xl font-black mb-4">
                Why Nigerians Trust Checkstall
              </h2>
  
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                We focus on helping customers import safely and successfully.
              </p>
            </div>
  
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  title: 'Verified Suppliers',
                  desc: 'We help reduce the risk of dealing with fake suppliers or scammers.'
                },
                {
                  title: 'Affordable Shipping',
                  desc: 'Competitive shipping rates from China to Nigeria.'
                },
                {
                  title: 'Customer Support',
                  desc: 'Our team is available to assist you throughout the process.'
                },
                {
                  title: 'Business Growth',
                  desc: 'Import products cheaper and increase your profit margin.'
                },
                {
                  title: 'Easy Tracking',
                  desc: 'Track your goods from China to Nigeria with ease.'
                },
                {
                  title: 'Trusted By Many',
                  desc: 'Many Nigerians already use Checkstall for sourcing and shipping.'
                }
              ].map((item, index) => (
                <div
                  key={index}
                  className="bg-white rounded-3xl p-8 shadow-lg border border-gray-100"
                >
                  <div className="w-14 h-14 rounded-2xl bg-red-100 flex items-center justify-center mb-6">
                    <div className="w-6 h-6 rounded-full bg-[#DC143C]"></div>
                  </div>
  
                  <h3 className="text-2xl font-bold mb-4">
                    {item.title}
                  </h3>
  
                  <p className="text-gray-600 leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
  
        {/* CTA SECTION */}
        <section className="py-20 bg-[#DC143C] text-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl md:text-6xl font-black leading-tight mb-6">
              Ready To Start Importing From China?
            </h2>
  
            <p className="text-xl text-red-100 max-w-3xl mx-auto mb-10">
              Join thousands of Nigerians using Checkstall import to source and ship products from China.
            </p>
  
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a
                href="https://checkstall.com/downloads"
                className="bg-white text-[#DC143C] hover:bg-gray-100 px-8 py-4 rounded-2xl text-lg font-black transition-all"
              >
                Download App
              </a>
  
              <a
                href="https://app.checkstall.com"
                className="border-2 border-white hover:bg-white hover:text-[#DC143C] px-8 py-4 rounded-2xl text-lg font-black transition-all"
              >
                Visit Our Store
              </a>
            </div>
          </div>
        </section>
      </div>
    )
  }
  
